// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React from 'react';
import { Marker } from 'react-map-gl';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/system';
import { useAppSelector } from 'Hooks';
import { truncateMiddle } from 'Utils/fileNameUtils';
import type { FileFootprint } from '../footprintSelectors';
import { nwCorner } from './footprintGeoJson';
import stylesDef from './styles';

const CAPTION_MAX = 40;

interface ComponentProps {
  footprints: FileFootprint[];
  footprintsOn: boolean;
  numbers: Map<string, string>;
}

// HTML Markers, not symbol layers: the no-basemap map style carries no glyphs,
// and Markers survive the streets/satellite style swap.
// While the switch is on every box carries its extent NUMBER (D89) in the
// kind's color - at Tulsa-scale tile counts the old name chips overlapped
// into noise. A hover from EITHER side upgrades the chip to a caption with
// the number and the full name.
export const FootprintCornerLabels: React.FC<ComponentProps> = ({ footprints, footprintsOn, numbers }) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const hovered = useAppSelector((state) => state.fileSelection.hovered);

  // Captions follow hover from EITHER side (D71): a sidebar row hover names
  // its box on the map just like a map hover does, so the pane<->map link
  // reads the same in both directions.
  const hoveredKeys = new Set(
    hovered ? hovered.files.map((f) => `${f.kind}-${f.id}`) : [],
  );

  const captionText = (fp: FileFootprint): string => {
    const label = numbers.get(`${fp.kind}-${fp.id}`);
    const name = truncateMiddle(fp.name, CAPTION_MAX);
    return label ? `${label} - ${name}` : name;
  };

  // Every hovered footprint gets a caption at its own NW corner; overlapping
  // footprints with the SAME corner (stacked tile sets) merge into one card,
  // one name per line, instead of piling markers on top of each other.
  const captionGroups = new Map<string, { longitude: number; latitude: number; names: string[] }>();
  footprints.forEach((fp) => {
    if (!hoveredKeys.has(`${fp.kind}-${fp.id}`)) return;
    const [longitude, latitude] = nwCorner(fp.bounds);
    const key = `${longitude}|${latitude}`;
    const group = captionGroups.get(key) || { longitude, latitude, names: [] };
    group.names.push(captionText(fp));
    captionGroups.set(key, group);
  });

  return (
    <>
      {footprintsOn && footprints.map((fp) => {
        if (hoveredKeys.has(`${fp.kind}-${fp.id}`)) return null;
        const [longitude, latitude] = nwCorner(fp.bounds);
        const label = numbers.get(`${fp.kind}-${fp.id}`) || truncateMiddle(fp.name, CAPTION_MAX);
        return (
          <Marker key={`${fp.kind}-${fp.id}`} longitude={longitude} latitude={latitude} anchor="top-left">
            <Box sx={styles.cornerLabel}>
              <Typography variant="body2" sx={styles.cornerLabelText}>
                {label}
              </Typography>
            </Box>
          </Marker>
        );
      })}
      {Array.from(captionGroups.entries()).map(([key, group]) => (
        <Marker key={key} longitude={group.longitude} latitude={group.latitude} anchor="top-left">
          <Box sx={[styles.cornerLabel, styles.cornerLabelHovered]}>
            {group.names.map((name) => (
              <Typography key={name} variant="body2" sx={styles.cornerLabelText}>
                {name}
              </Typography>
            ))}
          </Box>
        </Marker>
      ))}
    </>
  );
};

export default FootprintCornerLabels;
